const CANONICAL_DEVICE_ROLE_ORDER = ['edge', 'core', 'distribution', 'access', 'wireless-ap', 'firewall', 'unknown/mixed'] as const;

const CANONICAL_DEVICE_ROLE_LABELS: Record<string, string> = {
  edge: 'Edge',
  core: 'Core',
  distribution: 'Distribution',
  access: 'Access',
  'wireless-ap': 'Wireless AP',
  firewall: 'Firewall',
  'unknown/mixed': 'Unknown/Mixed'
};

export const normalizeDeviceRole = (value?: string | null): string =>
  value
    ?.trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-') ?? '';

export const deviceRoleSortRank = (value?: string | null): number => {
  const normalized = normalizeDeviceRole(value);
  if (!normalized) {
    return CANONICAL_DEVICE_ROLE_ORDER.length + 1;
  }
  const idx = CANONICAL_DEVICE_ROLE_ORDER.indexOf(normalized as (typeof CANONICAL_DEVICE_ROLE_ORDER)[number]);
  return idx === -1 ? CANONICAL_DEVICE_ROLE_ORDER.length : idx;
};

export const formatDeviceRoleLabel = (value?: string | null): string => {
  const normalized = normalizeDeviceRole(value);
  if (!normalized) {
    return '';
  }
  const known = CANONICAL_DEVICE_ROLE_LABELS[normalized];
  if (known) {
    return known;
  }
  return normalized
    .split('/')
    .map((segment) =>
      segment
        .split('-')
        .map((token) => (token ? token.charAt(0).toUpperCase() + token.slice(1) : token))
        .join('-')
    )
    .join('/');
};

export const deviceRoleMatches = (role?: string | null, filter?: string | null): boolean => {
  const normalizedFilter = normalizeDeviceRole(filter);
  if (!normalizedFilter || normalizedFilter === 'all') {
    return true;
  }
  return normalizeDeviceRole(role) === normalizedFilter;
};

export const isWirelessApRole = (role?: string | null): boolean => normalizeDeviceRole(role) === 'wireless-ap';

export const sortDeviceRoles = (roles: Iterable<string>, includeKnown = false): string[] => {
  const byNormalized = new Map<string, string>();

  for (const role of roles) {
    const normalized = normalizeDeviceRole(role);
    if (!normalized || byNormalized.has(normalized)) {
      continue;
    }
    byNormalized.set(normalized, role.trim());
  }

  if (includeKnown) {
    for (const role of CANONICAL_DEVICE_ROLE_ORDER) {
      if (!byNormalized.has(role)) {
        byNormalized.set(role, CANONICAL_DEVICE_ROLE_LABELS[role]);
      }
    }
  }

  return Array.from(byNormalized.entries())
    .sort(([normalizedA, rawA], [normalizedB, rawB]) => {
      const rankA = CANONICAL_DEVICE_ROLE_ORDER.indexOf(normalizedA as (typeof CANONICAL_DEVICE_ROLE_ORDER)[number]);
      const rankB = CANONICAL_DEVICE_ROLE_ORDER.indexOf(normalizedB as (typeof CANONICAL_DEVICE_ROLE_ORDER)[number]);
      const normalizedRankA = rankA === -1 ? CANONICAL_DEVICE_ROLE_ORDER.length : rankA;
      const normalizedRankB = rankB === -1 ? CANONICAL_DEVICE_ROLE_ORDER.length : rankB;
      if (normalizedRankA !== normalizedRankB) {
        return normalizedRankA - normalizedRankB;
      }
      return formatDeviceRoleLabel(rawA).localeCompare(formatDeviceRoleLabel(rawB), undefined, { sensitivity: 'base' });
    })
    .map(([, raw]) => raw);
};
